/**
 * 온라인 참여 모드와 same-origin 경로 (TASK-2026-004 SPEC 「포함 범위」 1·5).
 *
 * ## 왜 모드를 URL에서 읽는가
 *
 * 한 빌드가 두 가지로 쓰인다. 하나는 캠페인에 참여하는 **온라인 모드**이고,
 * 하나는 아무것도 서버로 보내지 않는 **로컬 연습 모드**다. 둘을 build 시각에
 * 가르면 로컬 연습용 번들과 온라인용 번들이 갈라져 "검사한 것"과 "참가자가
 * 받는 것"이 달라진다. 그래서 같은 번들이 실행 시각에 URL을 보고 갈린다.
 *
 * 캠페인 질의값이 없으면 로컬이다. **모드를 모르면 로컬로 떨어뜨린다** —
 * 잘못 판단했을 때 원격으로 자료가 나가는 쪽이 아니라 나가지 않는 쪽으로
 * 틀리게 만든다(중앙 명세 OF1-02).
 *
 * ## 왜 주소를 여기서만 만드는가
 *
 * 기존 로컬 흐름은 게임이 돌아갈 주소를 `respond` 질의값으로 받았다(열린
 * 리다이렉트를 막으려고 루프백 host만 허용했다). 온라인에서는 그 방식을 쓰지
 * 않는다. 참가자가 설문으로 돌아갈 곳은 항상 같은 origin의 `/collect.html`
 * 하나이며, 이 모듈이 **상대 경로만** 만든다. 임의 외부 URL이 들어올 자리가
 * 아예 없다.
 */

/** 수집·설문 페이지. 서버가 같은 origin에서 제공한다(중앙 명세 F1-03). */
export const COLLECT_PATH = '/collect.html';

/** 게임. 서버가 `/play/`로 제공한다. */
export const PLAY_PATH = '/play/';

export const CAMPAIGN_PARAM = 'campaign';
export const STEP_PARAM = 'step';
export const ENDING_PARAM = 'ending';

/** 서버 `contract.ts`의 `CAMPAIGN_ID_PATTERN`과 같은 모양이다. */
export const CAMPAIGN_ID_PATTERN = /^[a-z][a-z0-9-]{2,40}$/;

/** 참가자가 설문으로 돌아온 이유. 서버 `complete` 요청의 `ending`과 같은 값이다. */
export type Ending = 'all_cleared' | 'early_exit';

export const ENDINGS: readonly Ending[] = Object.freeze(['all_cleared', 'early_exit']);

/** collect 페이지의 단계. 게임에서 돌아올 때만 `survey`를 지정한다. */
export type CollectStep = 'survey';

export type OnlineMode =
  /** 아무것도 서버로 보내지 않는 연습 모드. */
  | { readonly kind: 'local' }
  /** 캠페인에 참여하는 온라인 모드. 동의·세션은 별개로 필요하다. */
  | { readonly kind: 'online'; readonly campaignId: string };

export const LOCAL: OnlineMode = Object.freeze({ kind: 'local' });

function params(search: string): URLSearchParams | undefined {
  try {
    return new URLSearchParams(search);
  } catch {
    return undefined;
  }
}

/**
 * 질의 문자열에서 모드를 읽는다.
 *
 * 캠페인 ID는 서버와 같은 패턴으로 검사한다. 모양이 틀리면 로컬이다 — 틀린
 * 캠페인으로 세션을 만들려 하면 서버가 404를 주고, 그 실패를 화면이 "참여할 수
 * 없다"로 설명하는 것보다 애초에 온라인으로 보지 않는 편이 단순하다.
 */
export function readMode(search: string): OnlineMode {
  const query = params(search);
  if (query === undefined) {
    return LOCAL;
  }
  const campaign = query.get(CAMPAIGN_PARAM)?.trim() ?? '';
  if (!CAMPAIGN_ID_PATTERN.test(campaign)) {
    return LOCAL;
  }
  return Object.freeze({ kind: 'online' as const, campaignId: campaign });
}

export function isOnline(mode: OnlineMode): mode is { kind: 'online'; campaignId: string } {
  return mode.kind === 'online';
}

/** 게임에서 돌아온 단계. 그 밖의 값은 없는 것으로 본다. */
export function readStep(search: string): CollectStep | undefined {
  return params(search)?.get(STEP_PARAM) === 'survey' ? 'survey' : undefined;
}

/** 돌아온 이유. 승인된 두 값만 통과한다. */
export function readEnding(search: string): Ending | undefined {
  const raw = params(search)?.get(ENDING_PARAM) ?? '';
  return ENDINGS.find((ending) => ending === raw);
}

/**
 * collect 페이지 주소. **항상 상대 경로다.**
 *
 * origin을 붙이지 않는 이유는 그것이 곧 신뢰 경계이기 때문이다. 상대 경로는
 * 지금 실행 중인 origin에서만 열리므로, 주소를 만드는 자리에서 외부로 나갈
 * 방법이 없다.
 */
export function collectUrl(
  options: { readonly campaignId: string; readonly step?: CollectStep; readonly ending?: Ending } = {
    campaignId: '',
  },
): string {
  const query = new URLSearchParams();
  if (CAMPAIGN_ID_PATTERN.test(options.campaignId)) {
    query.set(CAMPAIGN_PARAM, options.campaignId);
  }
  if (options.step !== undefined) {
    query.set(STEP_PARAM, options.step);
  }
  if (options.ending !== undefined) {
    query.set(ENDING_PARAM, options.ending);
  }
  const text = query.toString();
  return text === '' ? COLLECT_PATH : `${COLLECT_PATH}?${text}`;
}

/** 게임 주소. 캠페인을 실어 게임 쪽도 자기 모드를 알 수 있게 한다. */
export function playUrl(campaignId: string): string {
  if (!CAMPAIGN_ID_PATTERN.test(campaignId)) {
    return PLAY_PATH;
  }
  return `${PLAY_PATH}?${CAMPAIGN_PARAM}=${encodeURIComponent(campaignId)}`;
}
